import { lazy, Suspense, useState } from 'react';
import { Center, createTheme, Loader, MantineProvider } from '@mantine/core';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryPersistOptions } from './providers/queryPersist';
import { QueryPersistReadyProvider } from './providers/queryPersistReady';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Notifications } from '@mantine/notifications';
import { Homepage } from './pages/Homepage';
import { ProCourses } from './pages/ProCourses';

import { ProCourseCertificatePage } from './pages/ProCourseCertificatePage';
import { ProCoursePage } from './pages/ProCoursePage';

import '@mantine/charts/styles.css';
import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import 'mantine-datatable/styles.layer.css';
import './assets/animations.css';
import AuthLoadingScreen from './components/AuthLoadingScreen';
import Guest from './layouts/Guest';
import Main from './layouts/Main';
import Login from './pages/Login';
import LoginPartners from './pages/LoginPartners';
import Logout from './pages/Logout';
import {
  AuthProvider,
  useAuthUser,
  useAuthInitialized,
  useAuthLoading,
} from './providers/AuthProvider';
//import { useSyncAcrossTabs } from './store/useHeartRateStore';
import { ModalsProvider } from '@mantine/modals';
import AdminUser from './pages/admin/User/User';
import AdminUsers from './admin/Users';
import AdminLegacyUsers from './admin/LegacyUsers';
import AdminLegacyUserDetail from './admin/LegacyUserDetail';
import AdminLegacyUserMigrationPreview from './admin/LegacyUserMigrationPreview';
import AdminLegacySchema from './admin/LegacySchema';
import AdminLegacyTableBrowser from './admin/LegacyTableBrowser';
import AdminLegacyCatalogMapping from './admin/LegacyCatalogMapping';
import AdminLegacyMigrationHub from './admin/LegacyMigrationHub';
import AdminLegacyV0Users from './admin/LegacyV0Users';
import AdminLegacyV0UserDetail from './admin/LegacyV0UserDetail';
import { canAccessLegacyUsersAdmin } from './config/env';
import AdminUserCourses from './pages/admin/User/UserCourses';
import AdminUserDevices from './pages/admin/User/UserDevices';
import AdminUserProducts from './pages/admin/User/UserProducts';
import AdminUserAudios from './pages/admin/User/UserAudios';
import AdminUserAudioDetail from './pages/admin/User/UserAudioDetail';

const AdminUserLogs = lazy(() => import('./pages/admin/User/UserLogs'));
const AdminUserDeviceLogDetail = lazy(
  () => import('./pages/admin/User/UserDeviceLogDetail')
);
import { Accounts } from './pages/Accounts';
import { CompletedSessions } from './pages/CompletedSessions';
import ConfigurationCompleted from './pages/ConfigurationCompleted';
import CompletedSessionDetail from './pages/sessions/completed/CompletedSessionDetail';
import ExportSession from './pages/sessions/completed/ExportSession';
import { FreeApps } from './pages/FreeApps';
import { FreeCourses } from './pages/FreeCourses';
import Profile from './pages/Profile';
import Register from './pages/Register';
import SessionId from './pages/SessionDetails/SessionId';

import AdminAudios from './admin/Audios';
import AdminCourseChapters from './admin/CourseChapters';
import AdminCourseQuestions from './admin/CourseQuestions';
import AdminCourses from './admin/Courses';
import AdminProducts from './admin/Products';
import AdminVideos from './admin/Videos';
import AdminTranslations from './admin/Translations';
import AdminTranslationsAppDetail from './admin/TranslationsAppDetail';
import AdminExposureEvents from './admin/ExposureEvents';
import AdminBodyImageEvents from './admin/BodyImageEvents';
import AppPlaygroundHub from './admin/app-playground/AppPlaygroundHub';
import AppPlaygroundComingSoon from './admin/app-playground/AppPlaygroundComingSoon';
import AdminCertificateTemplates from './admin/AdminCertificateTemplates';
import AdminCertificateTemplateDetail from './admin/AdminCertificateTemplateDetail';

import { Devices } from './pages/Devices/Devices';
import { PublicSupportPage } from './pages/PublicSupportPage';
import { AblyProvider } from './providers/AblyProvider';
import {
  SessionNewPage,
  Sessions,
  SessionGroupsPage,
  SessionGroupNewPage,
  SessionGroupDetailPage,
  SessionGroupSessionDetailPage,
} from '@pages/sessions';
import Research from './pages/Research/Research';
import SpectralNormalizationPage from './pages/Research/SpectralNormalizationPage';
import SpectralNormalizationDetailPage from './pages/Research/SpectralNormalizationDetailPage';
import SpectralAnalysisHubPage from './pages/Research/SpectralAnalysisHubPage';
import SpectralNormalizationDocsPage from './pages/Research/SpectralNormalizationDocsPage';
import SpectralAnalysisDetailPage, {
  LegacyAnalysisPathRedirect,
} from './pages/Research/SpectralAnalysisDetailPage';
import { SessionRepairRedirectPage } from './pages/Research/SessionRepairRedirectPage';
import {
  PatientDetailPage,
  PatientEditPage,
  PatientsHubPage,
} from './features/patients';
import { LegacyPatientsRedirect } from './routes/LegacyPatientsRedirect';

const theme = createTheme({
  /*components: {
    Loader: Loader.extend({
      defaultProps: {
        loaders: { ...Loader.defaultLoaders, ring: RingLoader },
        type: 'ring',
      },
    }),
  },*/
});

import { RESEARCH_QUERY_CACHE_TTL_MS } from './features/research/shared/cache/constants';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: RESEARCH_QUERY_CACHE_TTL_MS,
      gcTime: RESEARCH_QUERY_CACHE_TTL_MS,
      retry: 2,
    },
  },
});

// Wrapper for Main with AblyProvider
function MainWithAbly() {
  return (
    <AblyProvider>
      <Main />
    </AblyProvider>
  );
}

const ALLOWED_RESEARCH_USER_IDS = new Set(['82', '66', '26', '35']);

function RequireResearchAccess({ children }: { children: React.ReactNode }) {
  const user = useAuthUser();
  const isAllowed = ALLOWED_RESEARCH_USER_IDS.has(String(user?.id ?? ''));

  if (!isAllowed) {
    return <Navigate to='/' replace />;
  }

  return <>{children}</>;
}

function RequireLegacyUsersAdminAccess({
  children,
}: {
  children: React.ReactNode;
}) {
  const user = useAuthUser();

  if (!canAccessLegacyUsersAdmin(user?.email)) {
    return <Navigate to='/admin/users' replace />;
  }

  return <>{children}</>;
}

// Internal component that uses auth hooks
function AppRoutes() {
  const isInitialized = useAuthInitialized();
  const isLoading = useAuthLoading();

  // Show loading screen until auth is initialized
  if (!isInitialized || isLoading) {
    return <AuthLoadingScreen />;
  }

  return (
    <Routes>
      {/* Guest Layout Routes - no Ably needed */}
      <Route element={<Guest />}>
        <Route path='/login' element={<Login />} />
        <Route
          path='/auth/partners/login'
          element={<Navigate to='/auth/login' replace />}
        />
        <Route path='/auth/login' element={<LoginPartners />} />
        <Route path='/register' element={<Register />} />
        <Route path='/logout' element={<Logout />} />
        {/* Add register or other guest routes here */}
      </Route>

      {/* Protected Routes with Ably */}
      <Route element={<MainWithAbly />}>
        <Route path='/' element={<Homepage />} />
        <Route path='/profile' element={<Profile />} />
        <Route path='/freeCourses' element={<FreeCourses />} />
        <Route path='/proCourses' element={<ProCourses />} />
        <Route
          path='/proCourses/:id/certificate'
          element={<ProCourseCertificatePage />}
        />
        <Route path='/proCourses/:id' element={<ProCoursePage />} />
        <Route path='/freeApps' element={<FreeApps />} />
        <Route
          path='/research'
          element={
            <RequireResearchAccess>
              <Research />
            </RequireResearchAccess>
          }
        />
        <Route
          path='/research/spectral-normalization/legacy'
          element={
            <RequireResearchAccess>
              <SpectralNormalizationPage />
            </RequireResearchAccess>
          }
        />
        <Route
          path='/research/spectral-normalization/legacy/:analysisId'
          element={
            <RequireResearchAccess>
              <SpectralNormalizationDetailPage />
            </RequireResearchAccess>
          }
        />
        <Route
          path='/research/spectral-normalization/documentation'
          element={
            <RequireResearchAccess>
              <SpectralNormalizationDocsPage />
            </RequireResearchAccess>
          }
        />
        <Route
          path='/research/spectral-normalization'
          element={
            <RequireResearchAccess>
              <SpectralAnalysisHubPage />
            </RequireResearchAccess>
          }
        />
        <Route
          path='/research/spectral-normalization/:analysisId'
          element={
            <RequireResearchAccess>
              <SpectralAnalysisDetailPage />
            </RequireResearchAccess>
          }
        />
        <Route
          path='/research/test-cnr'
          element={
            <RequireResearchAccess>
              <Navigate to='/research/spectral-normalization' replace />
            </RequireResearchAccess>
          }
        />
        <Route
          path='/research/test-cnr/:analysisId'
          element={
            <RequireResearchAccess>
              <LegacyAnalysisPathRedirect />
            </RequireResearchAccess>
          }
        />
        <Route
          path='/research/session-repair'
          element={
            <RequireResearchAccess>
              <SessionRepairRedirectPage />
            </RequireResearchAccess>
          }
        />
        <Route
          path='/research/session-repair/:sessionId'
          element={
            <RequireResearchAccess>
              <SessionRepairRedirectPage />
            </RequireResearchAccess>
          }
        />
        <Route path='/sessions' element={<Sessions />} />
        <Route path='/sessions/new' element={<SessionNewPage />} />
        <Route path='/sessions/group' element={<SessionGroupsPage />} />
        <Route path='/sessions/group/new' element={<SessionGroupNewPage />} />
        <Route
          path='/sessions/group/:groupId'
          element={<SessionGroupDetailPage />}
        />
        <Route
          path='/sessions/group/:groupId/sessions/:sessionId'
          element={<SessionGroupSessionDetailPage />}
        />
        <Route
          path='/sessions/completed'
          element={<Navigate to='/sessions' replace />}
        />
        <Route
          path='/sessions/completed/:id'
          element={<CompletedSessionDetail />}
        />
        <Route
          path='/sessions/completed/:id/export'
          element={<ExportSession />}
        />
        <Route path='/completedSessions' element={<CompletedSessions />} />
        <Route path='/sessions/:id' element={<SessionId />} />
        <Route path='/users' element={<PatientsHubPage />} />
        <Route path='/users/:userId' element={<PatientDetailPage />} />
        <Route path='/users/:userId/edit' element={<PatientEditPage />} />
        <Route path='/patients/*' element={<LegacyPatientsRedirect />} />
        <Route
          path='/completedSessions/:id'
          element={<ConfigurationCompleted />}
        />
        <Route path='/devices' element={<Devices />} />

        <Route path='/accounts' element={<Accounts />} />

        {/* TODO CHECK ROLE */}
        <Route
          path={'/admin/courseChapter'}
          element={<AdminCourseChapters />}
        />
        <Route
          path={'/admin/courseQuestions'}
          element={<AdminCourseQuestions />}
        />
        <Route
          path={'/admin/courses/certificate-templates'}
          element={<AdminCertificateTemplates />}
        />
        <Route path={'/admin/courses'} element={<AdminCourses />} />
        <Route path={'/admin/products'} element={<AdminProducts />} />
        <Route path={'/admin/audios'} element={<AdminAudios />} />
        <Route path={'/admin/videos'} element={<AdminVideos />} />
        <Route
          path={'/admin/certificate-templates'}
          element={
            <Navigate to='/admin/courses/certificate-templates' replace />
          }
        />
        <Route
          path={'/admin/certificate-templates/:id'}
          element={<AdminCertificateTemplateDetail />}
        />
        <Route path={'/admin/translations'} element={<AdminTranslations />} />
        <Route
          path={'/admin/translations/app/:appId'}
          element={<AdminTranslationsAppDetail />}
        />
        <Route path={'/admin/app-playground'} element={<AppPlaygroundHub />} />
        <Route
          path={'/admin/app-playground/exposure'}
          element={<AdminExposureEvents />}
        />
        <Route
          path={'/admin/app-playground/performance'}
          element={<AppPlaygroundComingSoon title='Performance' />}
        />
        <Route
          path={'/admin/app-playground/body-image'}
          element={<AdminBodyImageEvents />}
        />
        <Route
          path={'/admin/exposure-events'}
          element={<Navigate to='/admin/app-playground/exposure' replace />}
        />
        <Route path={'/admin/users'} element={<AdminUsers />} />
        <Route
          path={'/admin/legacy-migration'}
          element={
            <RequireLegacyUsersAdminAccess>
              <AdminLegacyMigrationHub />
            </RequireLegacyUsersAdminAccess>
          }
        />
        <Route
          path={'/admin/legacy-users'}
          element={
            <RequireLegacyUsersAdminAccess>
              <AdminLegacyUsers />
            </RequireLegacyUsersAdminAccess>
          }
        />
        <Route
          path={'/admin/legacy-users/schema'}
          element={
            <RequireLegacyUsersAdminAccess>
              <AdminLegacySchema />
            </RequireLegacyUsersAdminAccess>
          }
        />
        <Route
          path={'/admin/legacy-users/schema/tables/:tableName'}
          element={
            <RequireLegacyUsersAdminAccess>
              <AdminLegacyTableBrowser />
            </RequireLegacyUsersAdminAccess>
          }
        />
        <Route
          path={'/admin/legacy-users/mapping'}
          element={
            <RequireLegacyUsersAdminAccess>
              <AdminLegacyCatalogMapping />
            </RequireLegacyUsersAdminAccess>
          }
        />
        <Route
          path={'/admin/legacy-users/:id/preview'}
          element={
            <RequireLegacyUsersAdminAccess>
              <AdminLegacyUserMigrationPreview />
            </RequireLegacyUsersAdminAccess>
          }
        />
        <Route
          path={'/admin/legacy-users/:id'}
          element={
            <RequireLegacyUsersAdminAccess>
              <AdminLegacyUserDetail />
            </RequireLegacyUsersAdminAccess>
          }
        />
        <Route
          path={'/admin/legacy-v0-users'}
          element={
            <RequireLegacyUsersAdminAccess>
              <AdminLegacyV0Users />
            </RequireLegacyUsersAdminAccess>
          }
        />
        <Route
          path={'/admin/legacy-v0-users/:id'}
          element={
            <RequireLegacyUsersAdminAccess>
              <AdminLegacyV0UserDetail />
            </RequireLegacyUsersAdminAccess>
          }
        />
        <Route path={'/admin/users/:id'} element={<AdminUser />} />
        <Route
          path={'/admin/users/:id/courses'}
          element={<AdminUserCourses />}
        />
        <Route
          path={'/admin/users/:id/devices'}
          element={<AdminUserDevices />}
        />
        <Route
          path={'/admin/users/:id/logs/:deviceId/:logId'}
          element={
            <Suspense
              fallback={
                <Center h={240}>
                  <Loader />
                </Center>
              }
            >
              <AdminUserDeviceLogDetail />
            </Suspense>
          }
        />
        <Route
          path={'/admin/users/:id/logs'}
          element={
            <Suspense
              fallback={
                <Center h={240}>
                  <Loader />
                </Center>
              }
            >
              <AdminUserLogs />
            </Suspense>
          }
        />
        <Route
          path={'/admin/users/:id/products'}
          element={<AdminUserProducts />}
        />
        <Route path={'/admin/users/:id/audios'} element={<AdminUserAudios />} />
        <Route
          path={'/admin/users/:id/audios/:audioId'}
          element={<AdminUserAudioDetail />}
        />
      </Route>
    </Routes>
  );
}

function App() {
  //useSyncAcrossTabs();
  const [queryPersistReady, setQueryPersistReady] = useState(false);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={queryPersistOptions}
      onSuccess={() => setQueryPersistReady(true)}
    >
      <QueryPersistReadyProvider value={queryPersistReady}>
        <MantineProvider theme={theme}>
          <ModalsProvider>
            <Notifications />
            <Routes>
              {/* Completely public route - no auth required */}
              <Route path='/support' element={<PublicSupportPage />} />

              {/* All other routes require auth initialization */}
              <Route
                path='*'
                element={
                  <AuthProvider>
                    <AppRoutes />
                  </AuthProvider>
                }
              />
            </Routes>
          </ModalsProvider>
        </MantineProvider>
      </QueryPersistReadyProvider>
    </PersistQueryClientProvider>
  );
}

export default App;
